import {Injectable} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {ConfigProvider} from '@mydocma/common';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {isIosDevice} from '@docma-mm-core/utils/utils';
import {IMmConfig} from '@docma-mm-core/project/project.interface';
import {MmIosKeyboardScrollLockService} from './ios/keyboard-scroll-lock.service';
import {MmKeyboardFocusGuardService} from './ios/keyboard-focus-guard.service';
import {MmAndroidKeyboardDialogFitService} from './android/keyboard-dialog-fit.service';
import {MmKeyboardStateService} from './keyboard-state.service';
import {MmKeyboardTextareaGrowService} from './keyboard-textarea-grow.service';
import {MmKeyboardScrollerFreezeService} from './keyboard-scroller-freeze.service';
import {MmKeyboardFocusRevealService} from './keyboard-focus-reveal.service';
import {MmKeyboardPanHealService} from './ios/keyboard-pan-heal.service';
import {MmKeyboardModalFreezeService} from './keyboard-modal-freeze.service';
import {MmKeyboardScrollRoomService} from './keyboard-scroll-room.service';
import {MmKeyboardScrollHandoffService} from './keyboard-scroll-handoff.service';
import {MmKeyboardDropdownGuardService} from './keyboard-dropdown-guard.service';
import {MmKeyboardOverlayAnchorService} from './keyboard-overlay-anchor.service';

@Injectable({providedIn: 'root'})
export class MmMobileKeyboardService {

  constructor(
    private readonly configService: ConfigProvider<IMmConfig>,
    private readonly deviceService: DeviceService,
    private readonly iosScrollLock: MmIosKeyboardScrollLockService,
    private readonly iosFocusGuard: MmKeyboardFocusGuardService,
    private readonly androidDialogFit: MmAndroidKeyboardDialogFitService,
    private readonly keyboardState: MmKeyboardStateService,
    private readonly textareaGrow: MmKeyboardTextareaGrowService,
    private readonly scrollerFreeze: MmKeyboardScrollerFreezeService,
    private readonly focusReveal: MmKeyboardFocusRevealService,
    private readonly iosPanHeal: MmKeyboardPanHealService,
    private readonly modalFreeze: MmKeyboardModalFreezeService,
    private readonly scrollRoom: MmKeyboardScrollRoomService,
    private readonly scrollHandoff: MmKeyboardScrollHandoffService,
    private readonly dropdownGuard: MmKeyboardDropdownGuardService,
    private readonly overlayAnchor: MmKeyboardOverlayAnchorService,
  ) {}

  get isEnabled(): boolean {
    if (this.configService.getConfigData()?.mmConfig?.disableMobileKeyboardService === true) {
      return false;
    }
    // no soft keyboard to compensate for: visualViewport still shrinks on desktop pinch-zoom, which
    // would set mm-keyboard-open and swap every footer. DeviceService already counts iPadOS as tablet.
    return !this.deviceService.isDesktop();
  }

  init(): void {
    // shipped before the kill-switch existed (MMW-6005 + background scroll-lock) — the flag must only
    // disable what the keyboard subsystem introduced, not regress the production baseline
    if (Capacitor.isNativePlatform() && !isIosDevice()) {
      this.androidDialogFit.start();
    }
    this.iosScrollLock.init();
    if (!this.isEnabled) {
      return;
    }
    const isTablet = typeof document !== 'undefined' && this.deviceService.isTablet();
    if (isTablet) {
      document.documentElement.classList.add('mm-tablet-keyboard');
    }
    if (isIosDevice()) {
      // iOS-only: WebKit pans the document on focus (Android keyboards don't) — see the guard
      this.iosFocusGuard.start();
      this.iosPanHeal.start();
    }
    this.keyboardState.start();
    this.scrollerFreeze.start();
    if (isTablet) {
      this.scrollHandoff.start();
      // tablet-only: the wrapper renders a fullscreen popup instead of an inline panel on phones
      this.dropdownGuard.start();
    }
    this.focusReveal.start();
    this.overlayAnchor.start();
    if (isTablet) {
      // after keyboardState.start(): the closed-height capture guard needs the state listener to run first
      this.modalFreeze.start();
    }
    // phones too: the overlap is published for every focused scroller, but only surfaces with a padding
    // rule use it — on phone that is the DV activities tab alone
    this.scrollRoom.start();
  }
}
